import { Box, useTheme } from "@mui/material";
import type { FC } from "react";
import { MOTION, NEUTRAL, SELECTION_GLYPH, SURFACE } from "../../../theme/tokens";

type Props = {
	checked: boolean;
	disabled?: boolean;
	/** Grow the inner dot in (opacity + scale) as the ring fills — for standalone toggles. */
	animated?: boolean;
	/** Mark decorative when the surrounding element already conveys the state to AT (e.g. a `role="radio"` cell). */
	"aria-hidden"?: boolean;
};

/** Same footprint and edge as `RoundedCheckboxBox` (both read `SELECTION_GLYPH`), so the two columns line up. */
const { SIZE: BOX_SIZE, BORDER_WIDTH, RADIO_DOT_SIZE: DOT_SIZE, MARK_ENTER_SCALE: DOT_ENTER_SCALE } = SELECTION_GLYPH;

/**
 * The bare single-choice glyph — the radio sibling of `RoundedCheckboxBox`: an 18px ring that takes the
 * brand edge and a centred dot when checked. Purely visual; the host element owns the semantics
 * (`role="radio"` + `aria-checked`), exactly as the checkbox glyph leaves them to its host.
 */
export const RoundedRadioBox: FC<Props> = ({ checked, disabled, animated, "aria-hidden": ariaHidden }) => {
	const theme = useTheme();
	// Disabled controls render in muted greys so they read as unavailable rather than active.
	const checkedColor = disabled ? NEUTRAL[500] : theme.palette.primary.main;
	const uncheckedBorder = disabled ? NEUTRAL[300] : NEUTRAL[400];
	const transition = animated
		? {
				transition: `border-color ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
				"@media (prefers-reduced-motion: reduce)": { transition: "none" },
			}
		: {};

	return (
		<Box
			aria-hidden={ariaHidden}
			sx={{
				width: BOX_SIZE,
				height: BOX_SIZE,
				flex: "0 0 auto",
				borderRadius: "50%",
				boxSizing: "border-box",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				border: `${BORDER_WIDTH}px solid ${checked ? checkedColor : uncheckedBorder}`,
				backgroundColor: disabled ? NEUTRAL[100] : SURFACE,
				...transition,
			}}
		>
			<Box
				component="span"
				sx={{
					width: DOT_SIZE,
					height: DOT_SIZE,
					borderRadius: "50%",
					backgroundColor: checkedColor,
					opacity: checked ? 1 : 0,
					transform: checked ? "scale(1)" : `scale(${DOT_ENTER_SCALE})`,
					...(animated
						? {
								transition: `opacity ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}, transform ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
								"@media (prefers-reduced-motion: reduce)": { transition: "none" },
							}
						: {}),
				}}
			/>
		</Box>
	);
};
